import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PermissionsService, Permission } from '@services/permissions/permissions.service';
import { WorkScheduleEntry, WorkScheduleEntryType } from '@services/work-schedule/model';
import { forEach, includes, range, sortBy } from 'lodash';

type WorkScheduleEntryFormBase = {
  scheduleID: number;
  startTime: string;
  endTime: string;
};

type WorkScheduleEntryFormPayload = WorkScheduleEntryFormBase & {
  entryID?: number;
  daysOfWeek: string | null;
  startDate: string | null;
  endDate: string | null;
  effectiveDate?: string | null;
  expiresDate?: string | null;
  notes?: string | null;
  relativeDayName?: string | null;
  relativeDayOffset?: number | null;
  repeatAnnually: boolean;
};

export interface RetainedSettings {
  entryType?: WorkScheduleEntryType;
  effectiveDate?: string;
  expiresDate?: string;
  entireDay?: boolean;
  repeatAnnually?: boolean;
  startTime?: string;
  endTime?: string;
}

@Component({
  selector: 'app-entry-modal',
  templateUrl: './entry-modal.component.html',
  styleUrls: ['./entry-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonicModule,
    TranslateModule
  ]
})
export class EntryModalComponent implements OnInit {

  @Input() entry: WorkScheduleEntry | null = null; // If editing, pass existing entry
  @Input() scheduleID: number;
  // Optional: restrict which entry types are available
  // If not provided, all types are available
  @Input() allowedEntryTypes: WorkScheduleEntryType[] = null;
  // Optional: minimum date for date pickers (YYYY-MM-DD format)
  @Input() minDate: string = null;
  // Optional: retained settings from "Save and Add Another"
  @Input() retainedSettings: RetainedSettings = null;

  public entryForm: FormGroup;
  public entryType: WorkScheduleEntryType = WorkScheduleEntryType.Day_Of_Week;
  public isNew: boolean = true;
  public WorkScheduleEntryType = WorkScheduleEntryType;

  public daysOfWeek = [
    { value: 0, label: 'WORK_SCHEDULE.Sunday' },
    { value: 1, label: 'WORK_SCHEDULE.Monday' },
    { value: 2, label: 'WORK_SCHEDULE.Tuesday' },
    { value: 3, label: 'WORK_SCHEDULE.Wednesday' },
    { value: 4, label: 'WORK_SCHEDULE.Thursday' },
    { value: 5, label: 'WORK_SCHEDULE.Friday' },
    { value: 6, label: 'WORK_SCHEDULE.Saturday' }
  ];

  // US Federal Holidays - values must match backend WorkTime.pm %US_HOLIDAYS keys
  public usHolidays = [
    { value: 'New Year', label: 'WORK_SCHEDULE.Holiday_New_Years_Day' },
    { value: 'MLK Day', label: 'WORK_SCHEDULE.Holiday_MLK_Day' },
    { value: 'Presidents Day', label: 'WORK_SCHEDULE.Holiday_Presidents_Day' },
    { value: 'Memorial Day', label: 'WORK_SCHEDULE.Holiday_Memorial_Day' },
    { value: 'Juneteenth', label: 'WORK_SCHEDULE.Holiday_Juneteenth' },
    { value: 'Independence Day', label: 'WORK_SCHEDULE.Holiday_Independence_Day' },
    { value: 'Labor Day', label: 'WORK_SCHEDULE.Holiday_Labor_Day' },
    { value: 'Columbus Day', label: 'WORK_SCHEDULE.Holiday_Columbus_Day' },
    { value: 'Veterans Day', label: 'WORK_SCHEDULE.Holiday_Veterans_Day' },
    { value: 'Thanksgiving', label: 'WORK_SCHEDULE.Holiday_Thanksgiving' },
    { value: 'Christmas', label: 'WORK_SCHEDULE.Holiday_Christmas_Day' },
    { value: 'Good Friday', label: 'WORK_SCHEDULE.Holiday_Good_Friday' },
    { value: 'Easter', label: 'WORK_SCHEDULE.Holiday_Easter' }
  ];

  // Multi-select for days of week
  public selectedDays: Set<number> = new Set();

  // For effectiveDate/expiresDate restrictions
  public isAdmin: boolean = false;
  public todayDate: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private modalController: ModalController,
    public translate: TranslateService,
    private permissionsService: PermissionsService
  ) {
    // Calculate today's date for min date restrictions
    this.todayDate = new Date().toISOString().split('T')[0];

    // Check if user is admin (can backdate effectiveDate)
    this.isAdmin = this.permissionsService.canView(Permission.Admin);

    this.entryForm = this.formBuilder.group({
      // Common fields
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      entireDay: [false],

      // Date range fields (replaces specificDate)
      startDate: [''],
      endDate: [''],

      // Effective date range for recurring entries (day_of_week)
      effectiveDate: [''],
      expiresDate: [''],

      // Notes field
      notes: [''],

      // Relative date fields
      relativeDayName: [''],
      relativeDayOffset: [0],

      // Repeating
      repeatAnnually: [false]
    });
  }

  ngOnInit() {
    // Subscribe to entireDay changes to automatically set times
    this.entryForm.get('entireDay').valueChanges.subscribe((isEntireDay: boolean) => {
      if (isEntireDay) {
        this.entryForm.patchValue({
          startTime: '00:00',
          endTime: '23:59'
        }, { emitEvent: false }); // Prevent infinite loop
      }
    });

    // If allowed entry types is specified and current default isn't allowed, switch to first allowed
    if (this.allowedEntryTypes && this.allowedEntryTypes.length > 0) {
      if (!includes(this.allowedEntryTypes, this.entryType)) {
        this.entryType = this.allowedEntryTypes[0];
      }
    }

    // Apply retained settings from "Save and Add Another" (for new entries only)
    if (this.retainedSettings && !this.entry) {
      if (this.retainedSettings.entryType !== undefined) {
        this.entryType = this.retainedSettings.entryType;
      }
      this.entryForm.patchValue({
        effectiveDate: this.retainedSettings.effectiveDate || '',
        expiresDate: this.retainedSettings.expiresDate || '',
        entireDay: this.retainedSettings.entireDay || false,
        repeatAnnually: this.retainedSettings.repeatAnnually || false
      });

      // Restore times: entire day gets defaults, otherwise restore previous times
      if (this.retainedSettings.entireDay) {
        this.entryForm.patchValue({
          startTime: '00:00',
          endTime: '23:59'
        });
      } else if (this.retainedSettings.startTime || this.retainedSettings.endTime) {
        this.entryForm.patchValue({
          startTime: this.retainedSettings.startTime || '',
          endTime: this.retainedSettings.endTime || ''
        });
      }
    }

    if (this.entry) {
      this.isNew = false;

      // Determine entry type based on which fields are set
      // Backend returns entries with daysOfWeek array AND dayOfWeek (expanded)
      if (this.entry.daysOfWeek && Array.isArray(this.entry.daysOfWeek) && this.entry.daysOfWeek.length > 0) {
        this.entryType = WorkScheduleEntryType.Day_Of_Week;
        // Pre-select all days from the daysOfWeek array
        forEach(this.entry.daysOfWeek, (day: number) => this.selectedDays.add(day));
      } else if (this.entry.dayOfWeek !== null && this.entry.dayOfWeek !== undefined) {
        this.entryType = WorkScheduleEntryType.Day_Of_Week;
        // Fallback: pre-select the single day (for backward compatibility)
        this.selectedDays.add(this.entry.dayOfWeek);
      } else if (this.entry.startDate) {
        // Date range entry (was specificDate, now startDate/endDate)
        this.entryType = WorkScheduleEntryType.Specific_Date;
      } else if (this.entry.relativeDayName) {
        this.entryType = WorkScheduleEntryType.Relative_Date;
      }

      // Determine if this is an "entire day" entry
      // Handle times with or without seconds (00:00 or 00:00:00)
      const normalizeTime = (time: string) => time ? time.substring(0, 5) : '';
      const isEntireDay = normalizeTime(this.entry.startTime) === '00:00' && normalizeTime(this.entry.endTime) === '23:59';

      // Populate form with existing values
      this.entryForm.patchValue({
        startTime: this.entry.startTime || '',
        endTime: this.entry.endTime || '',
        entireDay: isEntireDay,
        startDate: this.entry.startDate || '',
        endDate: this.entry.endDate || '',
        effectiveDate: this.entry.effectiveDate || '',
        expiresDate: this.entry.expiresDate || '',
        notes: this.entry.notes || '',
        relativeDayName: this.entry.relativeDayName || '',
        relativeDayOffset: this.entry.relativeDayOffset || 0,
        repeatAnnually: this.entry.repeatAnnually || false
      });
    }

    this.updateValidators();
  }

  onEntryTypeChange() {
    this.updateValidators();

    // Set default all-day times for US Holidays
    if (this.entryType === WorkScheduleEntryType.Relative_Date) {
      const currentStartTime = this.entryForm.get('startTime').value;
      const currentEndTime = this.entryForm.get('endTime').value;

      // Only set defaults if times are empty
      if (!currentStartTime) {
        this.entryForm.patchValue({ startTime: '00:00' });
      }
      if (!currentEndTime) {
        this.entryForm.patchValue({ endTime: '23:59' });
      }

      // Default repeatAnnually and entireDay to true for holidays
      if (this.isNew) {
        this.entryForm.patchValue({ repeatAnnually: true, entireDay: true });
      }
    }
  }

  updateValidators() {
    // Clear all type-specific validators first
    this.entryForm.get('startDate').clearValidators();
    this.entryForm.get('endDate').clearValidators();
    this.entryForm.get('relativeDayName').clearValidators();

    // Add validators based on entry type
    if (this.entryType === WorkScheduleEntryType.Specific_Date) {
      this.entryForm.get('startDate').setValidators([Validators.required]);
      // endDate is optional - defaults to startDate if not provided
    } else if (this.entryType === WorkScheduleEntryType.Relative_Date) {
      this.entryForm.get('relativeDayName').setValidators([Validators.required]);
    }
    // Note: day_of_week validation is done via selectedDays.size check in isFormValid()

    // Update validity
    this.entryForm.get('startDate').updateValueAndValidity();
    this.entryForm.get('endDate').updateValueAndValidity();
    this.entryForm.get('relativeDayName').updateValueAndValidity();
  }

  // Day selection methods for multi-select
  toggleDay(dayValue: number) {
    if (this.selectedDays.has(dayValue)) {
      this.selectedDays.delete(dayValue);
    } else {
      this.selectedDays.add(dayValue);
    }
  }

  selectWeekdays() {
    this.selectedDays.clear();
    forEach(range(1, 6), (day) => this.selectedDays.add(day)); // Mon-Fri
  }

  selectWeekend() {
    this.selectedDays.clear();
    forEach([0, 6], (day) => this.selectedDays.add(day)); // Sun, Sat
  }

  selectAll() {
    this.selectedDays.clear();
    forEach(range(7), (day) => this.selectedDays.add(day));
  }

  clearDays() {
    this.selectedDays.clear();
  }

  isFormValid(): boolean {
    if (this.entryForm.invalid) {
      return false;
    }
    // For day_of_week, require at least one day selected
    if (this.entryType === WorkScheduleEntryType.Day_Of_Week && this.selectedDays.size === 0) {
      return false;
    }
    // Validate expiresDate is blank or after effectiveDate (for day_of_week and relative_date)
    if (this.entryType === WorkScheduleEntryType.Day_Of_Week || this.entryType === WorkScheduleEntryType.Relative_Date) {
      const effectiveDate = this.entryForm.value.effectiveDate;
      const expiresDate = this.entryForm.value.expiresDate;
      if (effectiveDate && expiresDate && expiresDate < effectiveDate) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if an entry type is allowed (for restricting options)
   */
  isEntryTypeAllowed(type: WorkScheduleEntryType): boolean {
    if (!this.allowedEntryTypes || this.allowedEntryTypes.length === 0) {
      return true; // All types allowed by default
    }
    return includes(this.allowedEntryTypes, type);
  }

  save() {
    if (!this.isFormValid()) {
      return;
    }

    const baseData: WorkScheduleEntryFormBase = {
      scheduleID: this.scheduleID,
      startTime: this.entryForm.value.startTime,
      endTime: this.entryForm.value.endTime
    };

    // Add type-specific fields
    if (this.entryType === WorkScheduleEntryType.Day_Of_Week) {
      // Send daysOfWeek as JSON array string (backend expects JSON string)
      const sortedDays = sortBy(Array.from(this.selectedDays), (day) => day);

      // Default effectiveDate to today if not set (for new entries)
      let effectiveDate = this.entryForm.value.effectiveDate || null;
      if (!effectiveDate && this.isNew) {
        effectiveDate = this.todayDate; // Default to today for new entries
      }

      const formData: WorkScheduleEntryFormPayload = {
        ...baseData,
        daysOfWeek: JSON.stringify(sortedDays),
        startDate: null,
        endDate: null,
        effectiveDate: effectiveDate,
        expiresDate: this.entryForm.value.expiresDate || null,
        notes: this.entryForm.value.notes || null,
        relativeDayName: null,
        relativeDayOffset: null,
        repeatAnnually: false
      };

      // If editing, include entryID
      if (!this.isNew && this.entry) {
        formData.entryID = this.entry.entryID;
      }

      // Return single entry with daysOfWeek array
      this.modalController.dismiss([formData], 'save');
    } else if (this.entryType === WorkScheduleEntryType.Specific_Date) {
      const formData: WorkScheduleEntryFormPayload = {
        ...baseData,
        daysOfWeek: null,
        startDate: this.entryForm.value.startDate,
        endDate: this.entryForm.value.endDate || this.entryForm.value.startDate, // Default to startDate if not set
        notes: this.entryForm.value.notes || null,
        relativeDayName: null,
        relativeDayOffset: null,
        repeatAnnually: this.entryForm.value.repeatAnnually
      };

      // If editing, include entryID
      if (!this.isNew && this.entry) {
        formData.entryID = this.entry.entryID;
      }

      this.modalController.dismiss([formData], 'save');
    } else if (this.entryType === WorkScheduleEntryType.Relative_Date) {
      // Default effectiveDate to today if not set (for new entries)
      let effectiveDate = this.entryForm.value.effectiveDate || null;
      if (!effectiveDate && this.isNew) {
        effectiveDate = this.todayDate; // Default to today for new entries
      }

      const formData: WorkScheduleEntryFormPayload = {
        ...baseData,
        daysOfWeek: null,
        startDate: null,
        endDate: null,
        effectiveDate: effectiveDate,
        expiresDate: this.entryForm.value.expiresDate || null,
        notes: this.entryForm.value.notes || null,
        relativeDayName: this.entryForm.value.relativeDayName,
        relativeDayOffset: this.entryForm.value.relativeDayOffset,
        repeatAnnually: this.entryForm.value.repeatAnnually
      };

      // If editing, include entryID
      if (!this.isNew && this.entry) {
        formData.entryID = this.entry.entryID;
      }

      this.modalController.dismiss([formData], 'save');
    }
  }

  /**
   * Save current entry and signal parent to reopen modal for another entry.
   * Retains: entry type, effective date, expires date, entire day setting
   */
  saveAndAddAnother() {
    if (!this.isFormValid()) {
      return;
    }

    // Build form data same as save(), but dismiss with different role
    const baseData: WorkScheduleEntryFormBase = {
      scheduleID: this.scheduleID,
      startTime: this.entryForm.value.startTime,
      endTime: this.entryForm.value.endTime
    };

    let formData: WorkScheduleEntryFormPayload;

    if (this.entryType === WorkScheduleEntryType.Day_Of_Week) {
      const sortedDays = sortBy(Array.from(this.selectedDays), (day) => day);
      let effectiveDate = this.entryForm.value.effectiveDate || null;
      if (!effectiveDate && this.isNew) {
        effectiveDate = this.todayDate;
      }

      formData = {
        ...baseData,
        daysOfWeek: JSON.stringify(sortedDays),
        startDate: null,
        endDate: null,
        effectiveDate: effectiveDate,
        expiresDate: this.entryForm.value.expiresDate || null,
        notes: this.entryForm.value.notes || null,
        relativeDayName: null,
        relativeDayOffset: null,
        repeatAnnually: false
      };
    } else if (this.entryType === WorkScheduleEntryType.Specific_Date) {
      formData = {
        ...baseData,
        daysOfWeek: null,
        startDate: this.entryForm.value.startDate,
        endDate: this.entryForm.value.endDate || this.entryForm.value.startDate,
        notes: this.entryForm.value.notes || null,
        relativeDayName: null,
        relativeDayOffset: null,
        repeatAnnually: this.entryForm.value.repeatAnnually
      };
    } else if (this.entryType === WorkScheduleEntryType.Relative_Date) {
      let effectiveDate = this.entryForm.value.effectiveDate || null;
      if (!effectiveDate && this.isNew) {
        effectiveDate = this.todayDate;
      }

      formData = {
        ...baseData,
        daysOfWeek: null,
        startDate: null,
        endDate: null,
        effectiveDate: effectiveDate,
        expiresDate: this.entryForm.value.expiresDate || null,
        notes: this.entryForm.value.notes || null,
        relativeDayName: this.entryForm.value.relativeDayName,
        relativeDayOffset: this.entryForm.value.relativeDayOffset,
        repeatAnnually: this.entryForm.value.repeatAnnually
      };
    }

    // Include retained settings for the next modal
    const retainedSettings: RetainedSettings = {
      entryType: this.entryType,
      effectiveDate: this.entryForm.value.effectiveDate,
      expiresDate: this.entryForm.value.expiresDate,
      entireDay: this.entryForm.value.entireDay,
      repeatAnnually: this.entryForm.value.repeatAnnually,
      startTime: this.entryForm.value.startTime,
      endTime: this.entryForm.value.endTime
    };

    this.modalController.dismiss({ entry: formData, retainedSettings }, 'saveAndAddAnother');
  }

  cancel() {
    this.modalController.dismiss(null, 'cancel');
  }
}
